import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CreateMeterDataIn } from '../../../api/api-reference';
import { CreateTariffDataIn } from '../tariff-setup/tariff-setup.component';

export interface TariffDevice {
  id: string;
  data: CreateTariffDataIn;
  dateAdded: Date;
}

@Injectable({
  providedIn: 'root'
})
export class TariffDataService {
  private tariffDevicesSubject = new BehaviorSubject<TariffDevice[]>([]);
  public tariffDevices$ = this.tariffDevicesSubject.asObservable();

  constructor() {}

  // Add a new tariff device
  addTariffDevice(data: CreateTariffDataIn): string {
    const currentDevices = this.tariffDevicesSubject.value;
    
    // Create a new instance and copy data properties
    const copiedData = new CreateTariffDataIn();
    Object.assign(copiedData, data);
    
    const newDevice: TariffDevice = {
      id: this.generateId(),
      data: copiedData,
      dateAdded: new Date()
    };
    
    const updatedDevices = [...currentDevices, newDevice];
    this.tariffDevicesSubject.next(updatedDevices);
    
    return newDevice.id;
  }

  // Get all tariff devices
  getTariffDevices(): TariffDevice[] {
    return this.tariffDevicesSubject.value;
  }

  // Get a specific tariff device by ID
  getTariffDevice(id: string): TariffDevice | undefined {
    return this.tariffDevicesSubject.value.find(device => device.id === id);
  }

  // Update a tariff device
  updateTariffDevice(id: string, data: CreateTariffDataIn): boolean {
    const currentDevices = this.tariffDevicesSubject.value;
    const deviceIndex = currentDevices.findIndex(device => device.id === id);
    
    if (deviceIndex > -1) {
      const updatedDevices = [...currentDevices];
      
      // Create a new instance and copy data properties
      const copiedData = new CreateTariffDataIn();
      Object.assign(copiedData, data);
      
      updatedDevices[deviceIndex] = {
        ...updatedDevices[deviceIndex],
        data: copiedData
      };
      this.tariffDevicesSubject.next(updatedDevices);
      return true;
    }
    
    return false;
  }

  // Delete a tariff device
  deleteTariffDevice(id: string): boolean {
    const currentDevices = this.tariffDevicesSubject.value;
    const updatedDevices = currentDevices.filter(device => device.id !== id);
    
    if (updatedDevices.length !== currentDevices.length) {
      this.tariffDevicesSubject.next(updatedDevices);
      return true;
    }
    
    return false;
  }

  // Clear all tariff devices
  clearAllTariffDevices(): void {
    this.tariffDevicesSubject.next([]);
  }

  // Get count of tariff devices
  getTariffDeviceCount(): number {
    return this.tariffDevicesSubject.value.length;
  }

  // Check if any devices exist
  hasDevices(): boolean {
    return this.tariffDevicesSubject.value.length > 0;
  }

  // Generate a unique ID for devices
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}